import { useState } from "react"
import { Spinner } from "@chakra-ui/react"

import { useSaveOnboardingStep } from "hooks/user"

interface Props {
    heading: string
    description: string
    options: { label: string; value: string }[]
    /** Profile field the selected option is saved to, e.g. "marketplace". */
    field: string
    nextStep: string
}

// Single-choice onboarding step, rendered inside Onboarding's shared AuthLayout.
const OptionStep: React.FC<Props> = ({ heading, description, options, field, nextStep }) => {
    const { saveStep } = useSaveOnboardingStep()
    const [selectedOption, setSelectedOption] = useState<string | null>(null)
    // Stays true after success: the step is swapped out once the cached user
    // updates, so the button must not flash back to enabled in between.
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleNext = () => {
        if (!selectedOption || isSubmitting) return
        setIsSubmitting(true)
        saveStep({ [field]: selectedOption, onboarding: nextStep }).catch(() => setIsSubmitting(false))
    }

    return (
        <div className="flex flex-col items-center justify-center">
            <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-800">{heading}</h1>
                <p className="text-gray-600 mt-2">{description}</p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
                {options.map((option) => (
                    <button
                        key={option.value}
                        onClick={() => setSelectedOption(option.value)}
                        disabled={isSubmitting}
                        className={`px-6 py-3 rounded-lg font-medium text-sm border transition-all duration-200 shadow-md disabled:cursor-not-allowed
                        ${
                            selectedOption === option.value
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 disabled:opacity-60 disabled:hover:bg-white"
                        }`}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            <button
                onClick={handleNext}
                className="inline-flex items-center justify-center gap-2 min-w-[120px] px-8 py-3 bg-primary text-white rounded-lg font-medium text-sm shadow-md hover:bg-secondary cursor-pointer transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary"
                disabled={!selectedOption || isSubmitting}
            >
                {isSubmitting && <Spinner size="sm" />}
                {isSubmitting ? "Saving..." : "Next"}
            </button>
        </div>
    )
}

export default OptionStep
